import { createContext, useCallback, useEffect, useMemo, useState } from "react";
import { Constant } from "../models/constant";
import { User, UserRoles } from "../models/user";
import { setUnauthorizedHandler } from "../services/api-client";
import { apiService } from "../services/api.service";

export interface AppContextInterface {
    user?: User,
    isAdmin: boolean;
    showLoader: boolean;
    setShowLoader: (value: boolean) => void;
    showLeftSide: boolean;
    setShowLeftSide: (value: boolean) => void;
    filters: string[];
    setFilter: (value: string) => void;
    removeFilter: (value?: string) => void;
    isLogged: boolean;
    setIsLogged: (value: boolean) => void;
    onLoading: boolean;
    setOnLoading: (value: boolean) => void;
}

const noop = () => { };

export const AppCtx = createContext<AppContextInterface>({
    user: undefined, isAdmin: false, filters: [],
    setFilter: noop, removeFilter: noop, showLoader: false, setShowLoader: noop,
    showLeftSide: true, setShowLeftSide: noop, isLogged: false, setIsLogged: noop,
    onLoading: false, setOnLoading: noop,
});

export interface AppProviderProps {
    children: any;
}

/**
 * Verifica che il token in localStorage sia presente e non scaduto. Prima bastava
 * la presenza della chiave: con un token scaduto l'app si considerava autenticata
 * e mostrava le schermate finché non arrivava il primo 401.
 */
const hasValidToken = () => {
    const token = localStorage.getItem(Constant.LS_ACCESS_TOKEN_KEY);
    if (!token) return false;

    try {
        const payload = JSON.parse(atob(token.split('.')[1] ?? ''));
        return typeof payload.exp !== 'number' || payload.exp * 1000 > Date.now();
    } catch {
        return false;
    }
};

const clearSession = () => {
    localStorage.removeItem(Constant.LS_ACCESS_TOKEN_KEY);
    localStorage.removeItem(Constant.LS_USER_KEY);
};

/**
 * Legge l'utente memorizzato, subito e non in un effetto.
 *
 * Se l'utente arrivasse solo dopo il primo render, chi decide autorizzazioni — RoleRoute —
 * lo vedrebbe assente e negherebbe l'accesso prima di sapere chi è: aprendo direttamente
 * l'indirizzo di una pagina riservata si finirebbe rimbalzati in home pur essendo
 * amministratori.
 */
const readStoredUser = (): User | undefined => {
    try {
        const stored = localStorage.getItem(Constant.LS_USER_KEY);
        return stored ? JSON.parse(stored) as User : undefined;
    } catch {
        return undefined;
    }
};

function AppProvider(props: AppProviderProps) {
    const [user, setUser] = useState<User | undefined>(readStoredUser);
    const [filters, setFilters] = useState<string[]>([]);
    const [showLoader, setShowLoader] = useState<boolean>(false);
    const [showLeftSide, setShowLeftSide] = useState<boolean>(true);
    const [isLogged, setIsLogged] = useState<boolean>(hasValidToken);
    const [pendingRequests, setPendingRequests] = useState<number>(0);

    // Il 401 non ricarica più la pagina: azzera la sessione e lascia che sia
    // ProtectedRoute a portare al login, mantenendo lo stato dell'app.
    useEffect(() => {
        setUnauthorizedHandler(() => {
            setUser(undefined);
            setIsLogged(false);
        });
        return () => setUnauthorizedHandler(null);
    }, []);

    useEffect(() => {
        if (!isLogged) {
            setUser(undefined);
            return;
        }

        // Rilettura al cambio di stato di login: dopo l'accesso l'utente in localStorage
        // è nuovo, e la lettura iniziale sincrona non poteva conoscerlo.
        setUser(readStoredUser());
    }, [isLogged])

    const setFilter = useCallback((value: string) =>
        setFilters(old => [...old, value]), []);

    const removeFilter = useCallback((value?: string) =>
        setFilters(old => value ? old.filter(x => x != value) : []), []);

    /**
     * Il loader globale conta le richieste in corso invece di essere un booleano:
     * con due chiamate concorrenti la prima che finiva spegneva anche il loader
     * dell'altra.
     */
    const setOnLoading = useCallback((value: boolean) =>
        setPendingRequests(count => Math.max(0, value ? count + 1 : count - 1)), []);

    const value = useMemo<AppContextInterface>(() => ({
        user,
        isAdmin: user?.role == UserRoles.Admin,
        filters, setFilter, removeFilter,
        showLoader, setShowLoader,
        showLeftSide, setShowLeftSide,
        isLogged, setIsLogged,
        onLoading: pendingRequests > 0, setOnLoading,
    }), [user, filters, setFilter, removeFilter, showLoader, showLeftSide, isLogged, pendingRequests, setOnLoading]);

    return (
        <AppCtx.Provider value={value}>{props.children}</AppCtx.Provider>
    )
};

export default AppProvider;
